<template>
  <div class="w-full h-full flex flex-col px-2 md:px-0">
    <tabs-wrapper contentClass="overflow-y-auto md:px-8 md:pt-2" class="h-full w-full">
      <!-- מה נסלק — the money story for the selected period -->
      <tab title="מה נסלק" description="כמה נכנס בתקופה, על מה שילמו לך והחיובים האחרונים" class="h-full w-full">
        <div class="w-full max-w-[1100px] flex flex-col gap-3 pb-10">
          <PaymentsAnalyticsTab
            v-if="dashboard && !isLoading"
            :data="dashboard.revenue"
            :periodLabel="periodLabel"
            :recentTransactions="recentTransactions"
            :hasPayMe="hasPayMe"
            @changePeriod="openPeriodPicker"
            @exportReports="openExports"
            @allTransactions="openAllTransactions"
            @refunded="fetchData(true)"/>
          <div v-else-if="isLoading && !isErrorState" class="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <Skeleton height="10rem" borderRadius="1.25rem"/>
            <Skeleton height="10rem" borderRadius="1.25rem"/>
            <Skeleton height="12rem" borderRadius="1.25rem"/>
            <Skeleton height="12rem" borderRadius="1.25rem"/>
          </div>
          <retry-loader v-else-if="isErrorState" :isErrorState="isErrorState" @retry-function="fetchData"/>
        </div>
      </tab>

      <!-- Every payment request ever made — links that went out and charges that landed -->
      <tab title="בקשות ותשלומים" description="כל בקשות התשלום שנשלחו והחיובים שבוצעו, מהחדש לישן" class="h-full w-full">
        <div class="w-full max-w-[1100px] pb-10">
          <RecentPaymentsTab />
        </div>
      </tab>

      <!-- העברות לבנק (PayMe withdrawals; join message for others) -->
      <tab title="העברות לבנק" description="הכסף שנסלק בדרך לחשבון הבנק — העברות שבוצעו וסכומים שממתינים" class="h-full w-full">
        <div class="w-full max-w-[1100px] pb-10">
          <PaymentsBankTransferTab :hasPayMe="hasPayMe"/>
        </div>
      </tab>
    </tabs-wrapper>
  </div>
</template>

<script lang="ts" setup>
import {computed, onMounted, ref} from 'vue';
import Skeleton from 'primevue/skeleton';
import {
  IAnalyticsDashboard,
  IPaymentTransactionRow,
  PaymentProvider,
  PaymentRequestStatus,
} from '@torim-app/types';
import {useDrawer} from '@torim-app/shared-components';

import Tab from '@app/components/admin/components/admin-tabs/Tab.vue';
import PaymentsAnalyticsTab from '@app/components/admin/analytics/PaymentsAnalyticsTab.vue';
import PaymentsBankTransferTab from '@app/components/admin/pages/payments/PaymentsBankTransferTab.vue';
import RecentPaymentsTab from '@app/components/admin/pages/payments/RecentPaymentsTab.vue';
import AllTransactionsDrawer from '@app/components/admin/pages/payments/AllTransactionsDrawer.vue';
import ExportReportsSheet from '@app/components/admin/pages/payments/ExportReportsSheet.vue';
import PeriodPickerSheet from '@app/components/admin/analytics/PeriodPickerSheet.vue';
import {presetRange, type IPeriodRange} from '@app/components/admin/analytics/period-presets';

import {useAdminStore} from '@app/stores';
import {AdminRouteNames} from '@app/router/route-names';
import {AnalyticsApi, PaymentsApi} from '@app/api/admin';
import {trackEvent} from '@app/composables/posthog';

const adminStore = useAdminStore();
const {setPage} = adminStore;
const {openDrawer} = useDrawer();

// Open to every admin: the charges tab shows recorded income for any business
// (or the catalogue estimate), and the bank-transfers tab pitches TimeIt Pay
// to non-PayMe businesses.
const hasPayMe = computed(() =>
  adminStore.getBusinessSiteData?.paymentIntegration?.paymentConfig?.provider === PaymentProvider.PAYME);

// ─── Period — controlled by the in-card pill button (design), defaults to החודש ──
const range = ref<IPeriodRange>(presetRange('month'));
const periodLabel = computed(() => range.value.label);
const customStart = ref<Date | undefined>(undefined);
const customEnd = ref<Date | undefined>(undefined);

const openPeriodPicker = () => {
  openDrawer(PeriodPickerSheet, {
    currentLabel: periodLabel.value,
    customStart: customStart.value,
    customEnd: customEnd.value,
    onPick: (picked: IPeriodRange & { customStart?: Date; customEnd?: Date }) => {
      range.value = {startDate: picked.startDate, endDate: picked.endDate, label: picked.label};
      customStart.value = picked.customStart;
      customEnd.value = picked.customEnd;
      fetchData();
    },
  }, {title: 'בחירת תקופה'});
};

// ─── Data — dashboard (totals + breakdowns) and recent charges with descriptions ──
const dashboard = ref<IAnalyticsDashboard | null>(null);
const recentTransactions = ref<IPaymentTransactionRow[]>([]);
const isLoading = ref(true);
const isErrorState = ref(false);

const PAID_STATUSES = [
  PaymentRequestStatus.Success,
  PaymentRequestStatus.Refunded,
  PaymentRequestStatus.PartiallyRefunded,
];

// `silent` refreshes the numbers in place (e.g. after a refund from an open drawer)
// without flashing the loading skeleton behind the drawer.
const fetchData = async (silent = false) => {
  try {
    isErrorState.value = false;
    if (!silent) isLoading.value = true;
    const [dashboardRes, paymentsRes] = await Promise.all([
      AnalyticsApi.fetchDashboard({startDate: range.value.startDate, endDate: range.value.endDate}),
      PaymentsApi.getPaymentsPageData({
        startDate: range.value.startDate,
        endDate: range.value.endDate,
        statuses: PAID_STATUSES,
      }),
    ]);
    dashboard.value = dashboardRes;
    recentTransactions.value = paymentsRes.transactions;
  } catch (e) {
    console.error(e);
    isErrorState.value = true;
  } finally {
    isLoading.value = false;
  }
};

const openExports = () => {
  trackEvent('payments_export_sheet_opened');
  openDrawer(ExportReportsSheet, {
    startDate: range.value.startDate,
    endDate: range.value.endDate,
    periodLabel: periodLabel.value,
  }, {title: 'ייצוא דוחות'});
};

// The full transactions list opens in a drawer, like the design's bottom sheet
const openAllTransactions = () => {
  trackEvent('payments_all_transactions_opened');
  openDrawer(AllTransactionsDrawer, {
    startDate: range.value.startDate,
    endDate: range.value.endDate,
    periodLabel: periodLabel.value,
    // refresh the totals behind the drawer in place when a refund goes through
    onRefunded: () => fetchData(true),
  }, {title: 'כל העסקאות', fullMobile: true});
};

onMounted(() => {
  setPage(AdminRouteNames.Payments);
  trackEvent('payments_page_viewed');
  fetchData();
});
</script>
